import { MarketPlaceTraderAnalysis } from "@lib/types/MarketPlace";
import {
  Drawer,
  Descriptions,
  Skeleton,
  Typography,
  Divider,
  Image,
  List,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import styled from "styled-components";
import dayjs from "dayjs";
import { LanguageLabel } from "@lib/components/MiniComponents/Others";
import { AppJson, AppTag } from "@lib/components/AppComponents";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  item: MarketPlaceTraderAnalysis | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 7 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const MarketPlaceTraderAnalysisDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={"common.previewTitle"}
          values={{
            title: messages["sidebar.marketPlace.traderAnalyses"] as string,
          }}
        />
      }
      onClose={onClose}
      open={open}
      width={600}
      id="market-place-trader-analysis-drawer"
    >
      {!item ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: item?.id }}>
                {shortenString(item?.id, 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdAt" />}
            >
              {item?.createdAt
                ? dayjs(item.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdBy" />}
            >
              {item?.createdBy || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedAt" />}
            >
              {item?.updatedAt
                ? dayjs(item.updatedAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedBy" />}
            >
              {item?.updatedBy || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Symbol">
              <AppTag color="blue">{item?.symbol}</AppTag>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.language" />}
            >
              {item?.language ? (
                <LanguageLabel languageCode={item.language} />
              ) : (
                "-"
              )}
            </Descriptions.Item>
          </StyledDescriptions>

          {item?.analysis && item.analysis.length > 0 && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  Analysis
                </Typography.Title>
                <List
                  size="small"
                  bordered
                  dataSource={item.analysis}
                  renderItem={(a) => <List.Item>{a}</List.Item>}
                />
              </div>
            </>
          )}

          {item?.imageUrl && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  <FormattedMessage id="common.image" />
                </Typography.Title>
                <Image
                  src={item.imageUrl}
                  alt="Trader Analysis Image"
                  style={{ maxWidth: "100%" }}
                />
              </div>
            </>
          )}

          {item?.extraData && Object.keys(item.extraData).length > 0 && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  Extra Data
                </Typography.Title>
                <AppJson src={item.extraData} />
              </div>
            </>
          )}
        </>
      )}
    </StyledDrawer>
  );
};
